import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
  type ComponentProps,
  type RefObject,
} from "react"

import { SearchInput } from "@coval/ui"

import { useSearchShortcut } from "@/hooks/useSearchShortcut"

type QuerySearchInputProps = Omit<
  ComponentProps<typeof SearchInput>,
  "value" | "onChange" | "onClear" | "ref"
> & {
  inputRef: RefObject<HTMLInputElement | null>
  value: string
  onValueChange: (value: string) => void
  onClear?: () => void
  searchLabel: string
  resetKey?: string | number
}

const SEARCH_DEBOUNCE_MS = 500

/** Keeps draft keystrokes local so applying a query does not block typing. */
export function QuerySearchInput({
  inputRef,
  value,
  onValueChange,
  onClear,
  searchLabel,
  resetKey,
  ...props
}: QuerySearchInputProps) {
  const [draft, setDraft] = useState(value)
  const [, startTransition] = useTransition()
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastEmitted = useRef(value)
  const previousResetKey = useRef(resetKey)
  const changeRef = useRef(onValueChange)
  changeRef.current = onValueChange

  const cancelPending = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  useEffect(() => {
    const wasReset = previousResetKey.current !== resetKey
    previousResetKey.current = resetKey
    if (wasReset || value !== lastEmitted.current) {
      cancelPending()
      lastEmitted.current = value
      setDraft(value)
    }
  }, [value, resetKey, cancelPending])

  useEffect(() => () => cancelPending(), [cancelPending])

  const clearSearch = () => {
    cancelPending()
    lastEmitted.current = ""
    setDraft("")
    if (onClear) {
      onClear()
    } else {
      changeRef.current("")
    }
  }

  useSearchShortcut({
    enabled: !props.disabled,
    inputRef,
    label: searchLabel,
    value: draft,
    onClear: clearSearch,
  })

  return (
    <SearchInput
      {...props}
      ref={inputRef}
      value={draft}
      onClear={clearSearch}
      onBlur={(event) => {
        // Commit before actions such as saving a view or exporting use the search.
        // Moving to the input's Clear button must cancel, rather than apply, a draft.
        const staysInInput =
          event.relatedTarget instanceof Node &&
          event.currentTarget.parentElement?.contains(event.relatedTarget)
        if (!staysInInput && timer.current !== null) {
          cancelPending()
          lastEmitted.current = draft
          changeRef.current(draft)
        }
        props.onBlur?.(event)
      }}
      onChange={(event) => {
        const nextValue = event.target.value
        cancelPending()
        setDraft(nextValue)
        if (!nextValue) {
          clearSearch()
          return
        }
        timer.current = setTimeout(() => {
          timer.current = null
          lastEmitted.current = nextValue
          startTransition(() => changeRef.current(nextValue))
        }, SEARCH_DEBOUNCE_MS)
      }}
    />
  )
}
